/**
 * GenerationView — standalone full-screen page for AI test case generation.
 *
 * Opened in a new browser tab by CreateTestCasePage. The live Playwright
 * browser stream fills the entire viewport, and a floating panel in the
 * bottom-right shows test cases as accordions with Xray step → Playwright
 * step mapping in plain English.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import Icons from "../icons";
import { friendlySelector, stepToEnglish, stepToScript } from "../stepEnglish";
import RunSocketNotice, { runSocketNote } from "./RunSocketNotice";
import { openRunSocket } from "../hooks/useRunSocket";
import { generationEventsPoll } from "../hooks/runPolls";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Step validation + normalization used to live here. The server keeps the
   run's cases in one stored record when it ends, and Save accepts that record:
   the server validates and saves them then. */

export function apiWsUrl(sessionId) {
  const base = new URL(API_BASE_URL, window.location.origin);
  const proto = base.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${base.host}/api/ws/generate/${sessionId}`;
}

/* Plain-English step descriptions moved to ../stepEnglish so the generation
   preview, the run view and the board step lists share one renderer. */

/* Convert a raw act event to plain English for the live feed. */
function actToEnglish(tool, args) {
  const action = (tool || "").toLowerCase();
  const a = args || {};
  if (action === "fill") return `Enter "${a.value || ""}" in ${a.selector || "a field"}`;
  if (action === "click") return `Click on ${friendlySelector(a.selector)}`;
  if (action === "press") return `Press ${a.key || a.value || "a key"}`;
  if (action === "selectoption" || action === "selectdropdown")
    return `Select "${a.value || ""}" from ${friendlySelector(a.selector)}`;
  if (action === "observe" || action === "observe_page") return "Observing the page…";
  return `${tool || "action"} ${Object.values(a).join(" ")}`.trim();
}

/* ------------------------------------------------------------------ */
/*  Floating Panel                                                     */
/* ------------------------------------------------------------------ */
function FloatingPanel({
  config, authoredCases, liveActions, done, saving, saveError, cancelling, usage,
  moduleQueue, queueIndex, onSave, onCancel, onAddStep, collapsed, onToggleCollapse,
}) {
  const listEndRef = useRef(null);
  const liveEndRef = useRef(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [newStepText, setNewStepText] = useState({});

  /* ---- Drag to move ---------------------------------------------------- */
  /* The panel can cover the part of the live browser you need to watch, so
     it can be dragged anywhere in the tab by its header. `pos` is the
     top-left corner in px; null means the default bottom-right dock.
     Double-clicking the header docks it back. */
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const [pos, setPos] = useState(null);

  const clamp = useCallback((x, y) => {
    const el = panelRef.current;
    const w = el ? el.offsetWidth : 0;
    const h = el ? el.offsetHeight : 0;
    return {
      x: Math.min(Math.max(0, x), Math.max(0, window.innerWidth - w)),
      y: Math.min(Math.max(0, y), Math.max(0, window.innerHeight - h)),
    };
  }, []);

  const onDragStart = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest("button")) return; // header buttons still click normally
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };
  const onDragMove = (e) => {
    if (!dragRef.current) return;
    setPos(clamp(e.clientX - dragRef.current.dx, e.clientY - dragRef.current.dy));
  };
  const onDragEnd = (e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
  };

  /* Keep a moved panel on-screen when the window shrinks or the panel grows
     (expanding, new test cases arriving). */
  useEffect(() => {
    if (!pos) return undefined;
    const reclamp = () =>
      setPos((p) => {
        if (!p) return p;
        const n = clamp(p.x, p.y);
        return n.x === p.x && n.y === p.y ? p : n;
      });
    window.addEventListener("resize", reclamp);
    let ro;
    if (typeof ResizeObserver !== "undefined" && panelRef.current) {
      ro = new ResizeObserver(reclamp);
      ro.observe(panelRef.current);
    }
    return () => {
      window.removeEventListener("resize", reclamp);
      ro?.disconnect();
    };
  }, [pos, clamp]);

  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [authoredCases.length]);
  useEffect(() => { liveEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [liveActions.length]);

  const toggleCase = (idx) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  const currentModule = moduleQueue[queueIndex];
  const totalCases = authoredCases.length;

  return (
    <div
      ref={panelRef}
      className={[
        "fixed z-50 flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200",
        pos ? "" : "bottom-4 right-4",
      ].join(" ")}
      style={{
        width: collapsed ? 320 : 520,
        maxHeight: collapsed ? "auto" : "80vh",
        ...(pos ? { left: pos.x, top: pos.y } : {}),
      }}
    >
      {/* Header — also the drag handle */}
      <div
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
        onDoubleClick={(e) => { if (!e.target.closest("button")) setPos(null); }}
        title="Drag to move · double-click to dock bottom-right"
        className="flex cursor-grab touch-none select-none items-center justify-between gap-2 border-b border-slate-200 bg-gradient-to-r from-brand-700 to-brand-600 px-4 py-3 active:cursor-grabbing"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {/* Grip dots — signals the header can be dragged */}
            <Icons.Grip className="h-4 w-4 flex-none text-white/60" />
            <Icons.Spark className="h-4 w-4 flex-none text-white/80" />
            <h3 className="truncate text-sm font-semibold text-white">
              {config?.name || "Test Generation"}
            </h3>
          </div>
          {!collapsed && currentModule && !done && (
            <p className="mt-0.5 truncate text-[0.6875rem] text-white/70">
              {moduleQueue.length > 1
                ? `Module ${queueIndex + 1}/${moduleQueue.length} — ${currentModule.menu_label}`
                : currentModule.menu_label}
            </p>
          )}
        </div>
        <div className="flex flex-none items-center gap-1">
          <button type="button" onClick={onToggleCollapse} title={collapsed ? "Expand" : "Collapse"}
            className="rounded-md p-1 text-white/70 transition hover:bg-white/20 hover:text-white">
            {collapsed ? <Icons.ChevronUp className="h-4 w-4" /> : <Icons.ChevronDown className="h-4 w-4" />}
          </button>
          <button type="button" onClick={onCancel} disabled={cancelling}
            title={done ? "Cancel" : "Cancel generation"}
            className="rounded-md p-1 text-white/70 transition hover:bg-white/20 hover:text-white disabled:opacity-50">
            <Icons.X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Status bar */}
          <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[0.6875rem]">
            {!done ? (
              <span className="flex items-center gap-1.5 text-brand-700">
                <span className="h-2 w-2 animate-pulse rounded-full bg-brand-500" />
                Generating…
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-emerald-700">
                <Icons.Check className="h-3 w-3" /> Done
              </span>
            )}
            <span className="text-slate-500">
              {totalCases} test case{totalCases !== 1 ? "s" : ""} authored
            </span>
            {usage?.calls > 0 && (
              <span
                className="text-slate-400"
                title={
                  `Input ${usage.input.toLocaleString()} · ` +
                  `Output ${usage.output.toLocaleString()} ` +
                  `(thinking ${usage.thinking.toLocaleString()}) · ` +
                  `Cached-input ${usage.cached.toLocaleString()} · ` +
                  `${usage.calls} LLM call${usage.calls !== 1 ? "s" : ""}`
                }
              >
                · {usage.total.toLocaleString()} tokens
              </span>
            )}
          </div>

          {/* Live activity feed — shows while generating */}
          {!done && liveActions.length > 0 && (
            <div className="border-b border-slate-100 px-4 py-2">
              <p className="mb-1.5 text-[0.625rem] font-semibold uppercase tracking-wider text-slate-400">
                Live activity
              </p>
              <div className="max-h-24 overflow-y-auto">
                {liveActions.slice(-8).map((a, i) => (
                  <div key={i} className={[
                    "flex items-start gap-1.5 py-0.5 text-[0.6875rem]",
                    a.type === "error" ? "text-rose-600" : a.type === "act" ? "text-brand-700" : "text-slate-500",
                  ].join(" ")}>
                    <span className="mt-0.5 flex-none">{a.type === "error" ? <Icons.X className="h-3 w-3" /> : <Icons.ChevronRight className="h-3 w-3" />}</span>
                    <span className="min-w-0 flex-1">{a.text}</span>
                  </div>
                ))}
                <div ref={liveEndRef} />
              </div>
            </div>
          )}

          {/* Test cases — accordion list */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {totalCases === 0 ? (
              <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-400">
                <Icons.Spinner className="h-4 w-4 animate-spin" /> Waiting for the AI…
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {authoredCases.map((c, idx) => {
                  const isOpen = expanded.has(idx);
                  const steps = Array.isArray(c.steps) ? c.steps : [];
                  const xraySteps = Array.isArray(c.xray_steps) ? c.xray_steps : [];
                  const hasXray = xraySteps.length > 0;
                  const moduleTag = c.menu_label ? `[${c.menu_label}] ` : "";

                  // Group Playwright steps by source_step_no for Xray mapping
                  const byXrayStep = {};
                  if (hasXray) {
                    steps.forEach((s) => {
                      const n = s.source_step_no || 0;
                      (byXrayStep[n] = byXrayStep[n] || []).push(s);
                    });
                  }

                  return (
                    <div key={idx}>
                      {/* Case header */}
                      <button type="button" onClick={() => toggleCase(idx)}
                        className="flex w-full items-start gap-2.5 px-4 py-2.5 text-left transition hover:bg-slate-50">
                        <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-50 text-[0.625rem] font-bold text-brand-700">
                          {idx + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[0.8125rem] font-medium text-slate-800">
                            {moduleTag}{c.name}
                          </span>
                          <span className="block text-[0.6875rem] text-slate-400">
                            {steps.length} step{steps.length !== 1 ? "s" : ""}
                            {hasXray && ` · from ${xraySteps.length} test step${xraySteps.length !== 1 ? "s" : ""}`}
                          </span>
                        </span>
                        <Icons.ChevronRight className={[
                          "mt-1 h-3.5 w-3.5 flex-none text-slate-400 transition-transform",
                          isOpen ? "rotate-90" : "",
                        ].join(" ")} />
                      </button>

                      {/* Expanded steps */}
                      {isOpen && (
                        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-2">
                          {hasXray ? (
                            /* Xray mode: show source step → generated steps mapping */
                            <div className="space-y-3">
                              {xraySteps.map((xs) => {
                                const mapped = byXrayStep[xs.step_no] || [];
                                return (
                                  <div key={xs.step_no}>
                                    {/* Xray source step */}
                                    <div className="mb-1 flex items-start gap-2 text-[0.75rem]">
                                      <span className="mt-px flex h-5 min-w-[3.5rem] flex-none items-center justify-center rounded bg-violet-100 px-1.5 text-[0.5625rem] font-bold text-violet-700">
                                        Step {xs.step_no}
                                      </span>
                                      <span className="min-w-0 flex-1">
                                        <span className="font-medium text-slate-700">{xs.action || "(no action)"}</span>
                                        {xs.result && (
                                          <span className="ml-1 text-slate-400">— expected: {xs.result}</span>
                                        )}
                                      </span>
                                    </div>
                                    {/* Mapped Playwright steps */}
                                    {mapped.length > 0 ? (
                                      <ol className="ml-[4.25rem] space-y-0.5">
                                        {mapped.map((s, si) => (
                                          <li key={si} className="flex items-start gap-1.5 text-[0.6875rem] text-slate-600">
                                            <span className="mt-px flex h-3.5 w-3.5 flex-none items-center justify-center rounded bg-brand-100 text-[0.5rem] font-semibold text-brand-700">
                                              <Icons.ChevronRight className="h-2.5 w-2.5" />
                                            </span>
                                            <span className="min-w-0">
                                    {stepToEnglish(s)}
                                    {stepToScript(s) && (
                                      <span className="mt-0.5 block break-all font-mono text-[0.625rem] text-slate-400">
                                        ({stepToScript(s)})
                                      </span>
                                    )}
                                  </span>
                                          </li>
                                        ))}
                                      </ol>
                                    ) : (
                                      <p className="ml-[4.25rem] text-[0.625rem] italic text-amber-600">
                                        No Artifact step found.
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                              {/* Unmapped steps (source_step_no = 0 or null) */}
                              {(byXrayStep[0] || []).length > 0 && (
                                <div>
                                  <p className="mb-1 text-[0.625rem] font-semibold text-slate-400">Additional steps</p>
                                  <ol className="ml-[4.25rem] space-y-0.5">
                                    {byXrayStep[0].map((s, si) => (
                                      <li key={si} className="flex items-start gap-1.5 text-[0.6875rem] text-slate-600">
                                        <span className="mt-px flex h-3.5 w-3.5 flex-none items-center justify-center rounded bg-slate-200 text-[0.5rem] font-semibold text-slate-500"><Icons.ChevronRight className="h-2.5 w-2.5" /></span>
                                        <span className="min-w-0">
                                    {stepToEnglish(s)}
                                    {stepToScript(s) && (
                                      <span className="mt-0.5 block break-all font-mono text-[0.625rem] text-slate-400">
                                        ({stepToScript(s)})
                                      </span>
                                    )}
                                  </span>
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                            </div>
                          ) : (
                            /* Non-Xray: flat step list in plain English */
                            <ol className="space-y-1.5">
                              {steps.map((s, si) => (
                                <li key={si} className="flex items-start gap-2 text-[0.75rem] text-slate-600">
                                  <span className="mt-px flex h-4 w-4 flex-none items-center justify-center rounded bg-slate-200 text-[0.5625rem] font-semibold text-slate-500">
                                    {si + 1}
                                  </span>
                                  <span className="min-w-0">
                                    {stepToEnglish(s)}
                                    {stepToScript(s) && (
                                      <span className="mt-0.5 block break-all font-mono text-[0.625rem] text-slate-400">
                                        ({stepToScript(s)})
                                      </span>
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ol>
                          )}
                          {/* Add new step manually */}
                          {done && (
                            <div className="mt-2 flex items-center gap-1.5">
                              <span className="flex h-4 w-4 flex-none items-center justify-center rounded bg-slate-200 text-[0.5625rem] font-semibold text-slate-400">+</span>
                              <input
                                value={newStepText[idx] || ""}
                                onChange={(e) => setNewStepText((p) => ({ ...p, [idx]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && (newStepText[idx] || "").trim()) {
                                    onAddStep?.(idx, { action: "click", selector: null, value: null, description: newStepText[idx].trim() });
                                    setNewStepText((p) => ({ ...p, [idx]: "" }));
                                  }
                                }}
                                placeholder="Add new step…"
                                className="min-w-0 flex-1 rounded border border-dashed border-slate-300 bg-white px-2 py-1 text-[0.75rem] text-slate-700 placeholder-slate-400 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={listEndRef} />
              </div>
            )}
          </div>

          {/* Footer — once the run is done it is just Save / Cancel. Save
              returns to the Requirement Board where the run was started;
              Cancel discards the generated cases. */}
          <div className="border-t border-slate-200 bg-white px-4 py-3">
            {saveError && <p className="mb-2 text-xs font-medium text-rose-600">{saveError}</p>}
            <div className="flex items-center justify-end gap-2">
              {/* Always enabled while generating — this really stops the run. */}
              <button type="button" onClick={onCancel} disabled={cancelling || saving}
                className={[
                  "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:cursor-wait disabled:opacity-60",
                  done
                    ? "border-slate-200 text-slate-600 hover:bg-slate-50"
                    : "border-rose-200 text-rose-700 hover:bg-rose-50",
                ].join(" ")}>
                {cancelling && <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />}
                {cancelling ? "Cancelling…" : done ? "Cancel" : "Cancel generation"}
              </button>
              <button type="button" onClick={onSave} disabled={!done || saving || cancelling || totalCases === 0}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50">
                {saving ? (
                  <><Icons.Spinner className="h-3.5 w-3.5 animate-spin" /> Saving…</>
                ) : !done ? (
                  <><Icons.Spinner className="h-3.5 w-3.5 animate-spin" /> Generating…</>
                ) : (
                  <><Icons.Check className="h-3.5 w-3.5" /> Save</>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main GenerationView                                                */
/* ------------------------------------------------------------------ */
export default function GenerationView() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [moduleQueue, setModuleQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [frameUrl, setFrameUrl] = useState(null);
  const [authoredCases, setAuthoredCases] = useState([]);
  const [liveActions, setLiveActions] = useState([]);
  const [done, setDone] = useState(false);
  // Running LLM token totals for the whole generation, summed from `usage`
  // events (one delta per LLM call) the server emits.
  const [usage, setUsage] = useState({
    calls: 0, input: 0, output: 0, thinking: 0, cached: 0, total: 0,
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const wsRef = useRef(null);
  const [connection, setConnection] = useState("idle");
  const currentModuleRef = useRef(null);
  /* Every session this run started, in the order they fired. Save finds the
     record each one stored when it ended (by session id) and accepts it;
     Cancel declines them. */
  const sessionIdsRef = useRef([]);
  /* Set once Cancel is pressed (here, or on the opening screen). Stops the
     module queue advancing and catches a session that starts mid-cancel. */
  const cancelRequestedRef = useRef(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("cortexqa_generation");
      if (!raw) { setError("No generation config found."); return; }
      const parsed = JSON.parse(raw);
      setConfig(parsed);
      setModuleQueue(parsed.moduleQueue || []);
    } catch { setError("Invalid generation config."); }
  }, []);

  // Run generation: POST per module, connect WebSocket.
  useEffect(() => {
    if (!config || moduleQueue.length === 0) return undefined;
    const current = moduleQueue[queueIndex];
    if (!current) return undefined;
    currentModuleRef.current = current;

    let cancelled = false;
    let advanced = false;
    const advance = () => {
      if (advanced || cancelled || cancelRequestedRef.current) return;
      advanced = true;
      if (queueIndex + 1 < moduleQueue.length) {
        setFrameUrl(null);
        setQueueIndex((i) => i + 1);
      } else { setDone(true); }
    };

    const start = async () => {
      try {
        const isXray = config.sourceMode === "xray";
        const res = await fetch(`${API_BASE_URL}features/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: (config.name || "").trim(),
            menu_label: current.menu_label,
            menu_path: current.menu_path,
            source_mode: config.sourceMode || "manual",
            ticket: config.sourceMode === "jira" || isXray ? (config.jiraTicket || "").trim() : "",
            description: config.sourceMode === "manual" ? (config.description || "").trim() : "",
            github_commit: config.sourceMode === "github" ? (config.selectedCommit || "") : "",
            changelog_version: config.sourceMode === "github" ? (config.githubVersionSummary || "") : "",
            xray_test_keys: isXray ? (config.xrayTestKeys || []) : [],
            xray_story_key: isXray ? (config.xrayStoryKey || "") : "",
            /* Extra Xray context kinds (epic / test_set / test_plan /
               preconditions). Older callers never set it — empty is fine. */
            xray_context: isXray && Array.isArray(config.xrayContext) ? config.xrayContext : [],
            // Duplicate backstop: the API skips Xray tests that already have a
            // Playwright case unless the user explicitly overrode it.
            xray_allow_duplicates: !!config.xrayAllowDuplicates,
            ai_context: (config.aiContext || "").trim(),
            provider: config.provider || "openai",
            model: config.model || "",
            auto_login: config.autoLogin !== false,
            target_login: !!config.targetLogin,
            ui_config: config,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !body?.data?.session_id) {
          setError(`Failed to start: ${body?.detail || `HTTP ${res.status}`}`);
          advance();
          return;
        }
        sessionIdsRef.current.push(body.data.session_id);
        /* Tell the screen that opened this tab which server session is
           running, so its Cancel can stop it even after this tab is closed. */
        try {
          const bc = new BroadcastChannel("cortexqa_generation");
          bc.postMessage({ type: "session-started", sessionId: body.data.session_id });
          bc.close();
        } catch {}
        /* Cancel was pressed before the server answered — stop it now. */
        if (cancelRequestedRef.current) {
          fetch(`${API_BASE_URL}features/sessions/${body.data.session_id}/cancel`, { method: "POST" }).catch(() => {});
          return;
        }
        /* A drop reads the session's events over HTTP (no reconnect: the
           server's handler for a dropped socket still waits on the run's
           queue and takes the next event, which a reconnected stream would
           miss; the event list has every one). The next module starts only
           once this one has ended (or is lost). */
        wsRef.current = openRunSocket(apiWsUrl(body.data.session_id), {
          onEvent: (msg) => handleEvent(msg),
          onStatus: setConnection,
          poll: generationEventsPoll(body.data.session_id),
          retries: 0,
          onEnd: ({ reason }) => {
            setConnection(reason === "lost" ? "lost" : "idle");
            advance();
          },
        });
      } catch (e) {
        if (!cancelled) { setError(`Network error: ${e.message}`); advance(); }
      }
    };
    start();
    return () => {
      cancelled = true;
      if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, queueIndex, moduleQueue.length]);

  const handleEvent = useCallback((evt) => {
    if (!evt?.type) return;
    switch (evt.type) {
      case "frame":
        if (evt.data_url) setFrameUrl(evt.data_url);
        break;
      case "info":
        if (evt.message) setLiveActions((p) => [...p.slice(-49), { type: "info", text: evt.message }]);
        break;
      case "act":
        setLiveActions((p) => [...p.slice(-49), { type: "act", text: actToEnglish(evt.tool, evt.args) }]);
        break;
      case "testcase": {
        const fullSteps = Array.isArray(evt.steps) ? evt.steps : [];
        const mod = currentModuleRef.current;
        setAuthoredCases((p) => [...p, {
          name: evt.name,
          category: evt.category,
          steps: fullSteps,
          menu_key: mod?.menu_key || "",
          menu_label: mod?.menu_label || "",
          menu_path: mod?.menu_path || [],
          xray_steps: evt.xray_steps || null,
          xray_test_key: evt.xray_test_key || null,
          source_meta: evt.source_meta || null,
        }]);
        break;
      }
      case "done": setDone(true); break;
      case "usage":
        setUsage((u) => ({
          calls: u.calls + (evt.calls || 0),
          input: u.input + (evt.input || 0),
          output: u.output + (evt.output || 0),
          thinking: u.thinking + (evt.thinking || 0),
          cached: u.cached + (evt.cached || 0),
          total: u.total + (evt.total || 0),
        }));
        break;
      case "error":
        if (evt.message) setLiveActions((p) => [...p.slice(-49), { type: "error", text: evt.message }]);
        break;
      default: break;
    }
  }, []);

  const addStep = useCallback((caseIdx, step) => {
    setAuthoredCases((prev) =>
      prev.map((c, i) => i === caseIdx ? { ...c, steps: [...(c.steps || []), step] } : c)
    );
  }, []);

  /* Save the authored cases. The server stores each run's cases in one record
     from its worker-thread finally; this click waits for that to land (worst
     case: a few hundred ms after "done" arrives on the WS), then accepts each
     record, which saves the cases. */
  const save = useCallback(async () => {
    if (!config || saving || authoredCases.length === 0) return;
    setSaving(true);
    setSaveError("");
    try {
      /* Poll for the records the server writes in its finally block. Up
         to ~5s of 300ms ticks: generous for the race, cheap on the server. */
      const uq = config.userkey ? `?userkey=${encodeURIComponent(config.userkey)}` : "";
      const wanted = new Set(sessionIdsRef.current.filter(Boolean));
      const findMatches = (items) =>
        (items || []).filter((i) =>
          i.kind === "playwright_generation"
          && i.status === "pending"
          && wanted.has(i.payload?.session_id)
        );
      let matched = [];
      if (wanted.size > 0) {
        const deadline = Date.now() + 5000;
        while (Date.now() < deadline) {
          try {
            const r = await fetch(`${API_BASE_URL}inbox${uq}`);
            const b = await r.json().catch(() => ({}));
            matched = findMatches(b?.data?.items);
            if (matched.length >= wanted.size) break;
          } catch { /* keep polling */ }
          await new Promise((res) => setTimeout(res, 300));
        }
      }
      if (matched.length === 0) {
        /* Nothing landed yet — don't close; let the user retry or wait. */
        setSaveError("The generated tests are still being stored. Please try again in a moment.");
        return;
      }
      for (const i of matched) {
        const r = await fetch(`${API_BASE_URL}inbox/${encodeURIComponent(i.inboxkey)}/accept${uq}`, { method: "POST" });
        /* 409: already saved (Save pressed again) — fine. */
        if (!r.ok && r.status !== 409) {
          const b = await r.json().catch(() => ({}));
          throw new Error(b?.detail || `HTTP ${r.status}`);
        }
      }

      /* Tell the screen that opened this tab, so it can return to the
         Requirement Board on the same epic / story / tests. */
      try {
        const bc = new BroadcastChannel("cortexqa_generation");
        bc.postMessage({
          type: "saved",
          count: matched.length,
          storyKey: config.xrayStoryKey || config.jiraKey || "",
          xrayTestKeys: config.xrayTestKeys || [],
          cases: authoredCases.map((c) => ({
            name: c.name,
            menu_label: c.menu_label || "",
            xray_test_key: c.xray_test_key || c.source_meta?.xray_test_key || "",
            steps_count: Array.isArray(c.steps) ? c.steps.length : 0,
          })),
        });
        bc.close();
      } catch {}
      localStorage.removeItem("cortexqa_generation");
      window.close();
    } catch (e) { setSaveError(`Save failed: ${e.message}`); } finally { setSaving(false); }
  }, [config, saving, authoredCases]);

  /* Cancel — available at any moment. While the run is in flight it really
     stops it: the server cancels the agent, closes its browser and discards
     the cases (nothing is stored). Closing the WebSocket alone used to
     leave the server running and still filing results. Once the run is done
     the server has already stored the cases, so Cancel declines
     them there — Cancel means nothing is kept, before or after the run. */
  const handleCancel = useCallback(async (opts = {}) => {
    if (cancelRequestedRef.current) return;
    cancelRequestedRef.current = true;
    setCancelling(true);
    if (!done) {
      await Promise.all(
        sessionIdsRef.current.map((sid) =>
          fetch(`${API_BASE_URL}features/sessions/${encodeURIComponent(sid)}/cancel`, { method: "POST" })
            .catch(() => {})
        )
      );
    } else {
      const uq = config?.userkey ? `?userkey=${encodeURIComponent(config.userkey)}` : "";
      const wanted = new Set(sessionIdsRef.current.filter(Boolean));
      try {
        const r = await fetch(`${API_BASE_URL}inbox${uq}`);
        const b = await r.json().catch(() => ({}));
        const items = (b?.data?.items || []).filter(
          (i) => i.kind === "playwright_generation" && i.status === "pending" && wanted.has(i.payload?.session_id)
        );
        await Promise.all(
          items.map((i) =>
            fetch(`${API_BASE_URL}inbox/${encodeURIComponent(i.inboxkey)}/decline${uq}`, { method: "POST" })
              .catch(() => {})
          )
        );
      } catch { /* best effort — an undecided record is never saved */ }
    }
    if (wsRef.current) { try { wsRef.current.close(); } catch {} }
    localStorage.removeItem("cortexqa_generation");
    // The opener already knows when it asked for the cancel itself.
    if (!opts.fromOpener) {
      try { const bc = new BroadcastChannel("cortexqa_generation"); bc.postMessage({ type: "cancelled" }); bc.close(); } catch {}
    }
    window.close();
  }, [done, config]);

  /* The opening screen's "Cancel generation" button asks this tab to stop. */
  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel("cortexqa_generation");
      bc.onmessage = (evt) => {
        if (evt.data?.type === "cancel-request") handleCancel({ fromOpener: true });
      };
    } catch {}
    return () => { try { bc?.close(); } catch {} };
  }, [handleCancel]);

  if (error && !config) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-100 p-8">
        <div className="max-w-md rounded-xl bg-white p-6 text-center shadow-lg ring-1 ring-slate-200">
          <Icons.X className="mx-auto mb-3 h-8 w-8 text-rose-500" />
          <h1 className="text-lg font-semibold text-slate-900">Generation Error</h1>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <button type="button" onClick={() => window.close()}
            className="mt-4 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-slate-900">
      {runSocketNote(connection) && (
        <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-lg bg-card px-3 py-1.5 shadow">
          <RunSocketNotice status={connection} />
        </div>
      )}
      {frameUrl ? (
        <img src={frameUrl} alt="Live browser view" className="h-full w-full object-contain" />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 text-slate-400">
          <Icons.Spinner className="h-10 w-10 animate-spin text-brand-500" />
          <p className="text-sm font-medium">{done ? "Generation complete" : "Launching browser session…"}</p>
          {config?.name && <p className="text-xs text-slate-500">{config.name}</p>}
        </div>
      )}

      {config && (
        <FloatingPanel
          config={config} authoredCases={authoredCases} liveActions={liveActions}
          done={done} saving={saving} saveError={saveError} cancelling={cancelling} usage={usage}
          moduleQueue={moduleQueue} queueIndex={queueIndex}
          onSave={save} onCancel={() => handleCancel()} onAddStep={addStep}
          collapsed={collapsed} onToggleCollapse={() => setCollapsed((c) => !c)}
        />
      )}
    </div>
  );
}
